import { useEffect, useState } from 'react'
import type React from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { Modal } from '@/components/Modal'
import { AlertModal, AlertType } from '@/components/AlertModal'
import { Plug, Copy, Check, Trash2, Plus, BookOpen, Zap, Globe, Share2, ExternalLink } from 'lucide-react'

// --------------- Types ---------------

interface AiConnection {
  oauth_client_id: number
  client_id: string
  client_secret_prefix: string
  name: string
  description?: string
  scope: string
  status: string
  created_at: string
}

interface CreatedCredentials {
  client_id: string
  client_secret: string
  api_key: string
  auth_username: string
  auth_password: string
}

type Tab = 'connections' | 'ai-storefront' | 'chatgpt-app' | 'custom-gpt'

const MCP_BASE = 'https://mcp.tixxly.ai'

// --------------- Main Page ---------------

const REQUIRED_STEPS = ['connect_provider', 'sync_feeds', 'sync_experiences', 'connect_payment', 'accept_agreement']

export default function AiConnectionsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const vendorName = (user as any)?.vendorName || (user as any)?.companyName || 'Your Company'
  const isAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role?.toUpperCase() || '')
  const queryClient = useQueryClient()

  // Check onboarding status
  const { data: onboardingData } = useQuery({
    queryKey: ['onboarding-status', vendorId],
    queryFn: () => api.vendors.getOnboardingStatus(vendorId!.toString()),
    enabled: !!vendorId && !isAdmin,
  })

  const onboarding = (onboardingData as any)?.data?.data
  const prerequisitesMet = isAdmin
    || onboarding?.completed === true
    || !onboarding?.steps
    || REQUIRED_STEPS.every((req) => {
      const step = onboarding.steps.find((s: any) => s.id === req)
      return step?.completed
    })

  const [tab, setTab] = useState<Tab>('ai-storefront')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [credentials, setCredentials] = useState<CreatedCredentials | null>(null)
  const [createName, setCreateName] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [copied, setCopied] = useState<string | null>(null)
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean; type: AlertType; title: string; message: string; onConfirm?: () => void
  }>({ isOpen: false, type: 'info', title: '', message: '' })

  const { data: connectionsData, isLoading } = useQuery({
    queryKey: ['ai-connections', vendorId],
    queryFn: () => api.vendors.aiConnections.list(vendorId!.toString()),
    enabled: !!vendorId,
  })

  const connections: AiConnection[] = (connectionsData as any)?.data?.data || []

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      api.vendors.aiConnections.create(vendorId!.toString(), data),
    onSuccess: (response: any) => {
      const data = response?.data?.data
      if (data) {
        setCredentials({ client_id: data.client_id, client_secret: data.client_secret, api_key: data.api_key, auth_username: data.auth_username, auth_password: data.auth_password })
      }
      setShowCreateModal(false)
      setCreateName('')
      setCreateDescription('')
      queryClient.invalidateQueries({ queryKey: ['ai-connections'] })
    },
    onError: (error: any) => {
      setAlertModal({ isOpen: true, type: 'error', title: 'Error', message: error?.response?.data?.message || 'Failed to create connection' })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (clientId: string) => api.vendors.aiConnections.revoke(vendorId!.toString(), clientId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ai-connections'] }) },
  })

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }

  const handleRevoke = (conn: AiConnection) => {
    setAlertModal({
      isOpen: true, type: 'warning', title: 'Revoke AI Connection',
      message: `This will permanently revoke "${conn.name}" and its API key. Any apps using these credentials will stop working.`,
      onConfirm: () => { revokeMutation.mutate(conn.client_id); setAlertModal(prev => ({ ...prev, isOpen: false })) },
    })
  }

  // Tab styling
  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.625rem 1.25rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
    background: active ? 'var(--color-tixxly-navy)' : 'transparent',
    color: active ? 'var(--color-pure-white)' : 'var(--color-gray-500)',
    border: active ? 'none' : '1px solid var(--color-gray-200)',
    borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap', flexShrink: 0,
  })

  const pageContent = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', margin: 0 }}>
            AI Connections
          </h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginTop: '0.25rem' }}>
            Connect your catalog to ChatGPT, Claude, and other AI platforms.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '2px' }}>
        <button style={tabStyle(tab === 'ai-storefront')} onClick={() => setTab('ai-storefront')}>
          <Globe size={14} /> AI Storefront
        </button>
        <button style={tabStyle(tab === 'connections')} onClick={() => setTab('connections')}>
          <Plug size={14} /> Connections
        </button>
        <button style={tabStyle(tab === 'chatgpt-app')} onClick={() => setTab('chatgpt-app')}>
          <BookOpen size={14} /> ChatGPT Sharing
        </button>
        <button style={tabStyle(tab === 'custom-gpt')} onClick={() => setTab('custom-gpt')}>
          <Zap size={14} /> Custom GPT Guide
        </button>
      </div>

      {/* Tab content */}
      {tab === 'ai-storefront' && vendorId && (
        <AiStorefrontTab vendorId={vendorId.toString()} vendorName={vendorName} copied={copied} onCopy={copyToClipboard} />
      )}
      {tab === 'connections' && (
        <ConnectionsTab
          connections={connections}
          isLoading={isLoading}
          onCreateClick={() => setShowCreateModal(true)}
          onRevoke={handleRevoke}
          revokePending={revokeMutation.isPending}
        />
      )}
      {tab === 'chatgpt-app' && vendorId && (
        <ChatGptAppGuide vendorId={vendorId.toString()} vendorName={vendorName} copied={copied} onCopy={copyToClipboard} />
      )}
      {tab === 'custom-gpt' && (
        <CustomGptGuide vendorId={vendorId?.toString() || ''} vendorName={vendorName} connections={connections} copied={copied} onCopy={copyToClipboard} />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="New AI Connection">
          <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate({ name: createName, description: createDescription || undefined }) }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>Name</label>
              <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. ChatGPT Production" required
                style={{ width: '100%', padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)', border: '1px solid var(--color-gray-200)', borderRadius: '6px' }} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>Description (optional)</label>
              <input type="text" value={createDescription} onChange={e => setCreateDescription(e.target.value)} placeholder="What platform is this for?"
                style={{ width: '100%', padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)', border: '1px solid var(--color-gray-200)', borderRadius: '6px' }} />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setShowCreateModal(false)}
                style={{ padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', background: 'var(--color-gray-100)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="submit" disabled={!createName || createMutation.isPending}
                style={{ padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: !createName || createMutation.isPending ? 0.5 : 1 }}>
                {createMutation.isPending ? 'Creating...' : 'Create Connection'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Credentials modal */}
      {credentials && (
        <Modal isOpen={!!credentials} onClose={() => setCredentials(null)} title="AI Connection Created">
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-coral-red)', fontWeight: 'var(--font-weight-semibold)', marginBottom: '1rem', padding: '0.75rem', background: '#fef2f2', borderRadius: '6px' }}>
              Copy these credentials now. The Client Secret and API Key will not be shown again.
            </p>
            <CredentialField label="API Key (x-api-key)" value={credentials.api_key} copied={copied} onCopy={copyToClipboard} />
            <CredentialField label="OAuth Client ID" value={credentials.client_id} copied={copied} onCopy={copyToClipboard} />
            <CredentialField label="OAuth Client Secret" value={credentials.client_secret} copied={copied} onCopy={copyToClipboard} />
            <CredentialField label="Login Username" value={credentials.auth_username} copied={copied} onCopy={copyToClipboard} />
            <CredentialField label="Login Password" value={credentials.auth_password} copied={copied} onCopy={copyToClipboard} />
          </div>
          <div style={{ padding: '0.75rem', background: 'var(--color-gray-50)', borderRadius: '6px', marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: 0, lineHeight: 1.6 }}>
              <strong>MCP Server URL:</strong> <code>{MCP_BASE}/mcp</code><br />
              <strong>Authorization URL:</strong> <code>{MCP_BASE}/oauth/authorize</code><br />
              <strong>Token URL:</strong> <code>{MCP_BASE}/oauth/token</code><br />
              <strong>Token Auth Method:</strong> client_secret_post
            </p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setCredentials(null)}
              style={{ padding: '0.5rem 1.5rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Done
            </button>
          </div>
        </Modal>
      )}

      <AlertModal isOpen={alertModal.isOpen} type={alertModal.type} title={alertModal.title} message={alertModal.message}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))} onConfirm={alertModal.onConfirm} confirmText="Revoke" cancelText="Cancel" />
    </>
  )

  // Admins get all tabs
  if (isAdmin) return <div>{pageContent}</div>

  // Block access until onboarding prerequisites are met
  if (!prerequisitesMet) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', margin: 0 }}>AI Connections</h2>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginTop: '0.25rem' }}>
              Connect your catalog to ChatGPT, Claude, and other AI platforms.
            </p>
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: '3rem', background: 'var(--color-gray-50)', border: '1px dashed var(--color-gray-300)' }}>
          <Globe size={40} style={{ color: 'var(--color-gray-300)', marginBottom: '1rem' }} />
          <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-700)', margin: '0 0 0.5rem' }}>
            Complete onboarding first
          </h3>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1.25rem', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
            Connect your provider, sync your products, set up payments, and accept the supplier agreement before configuring your AI storefront.
          </p>
          <a href="/vendor" style={{
            display: 'inline-block', padding: '0.5rem 1.5rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)', borderRadius: '6px', textDecoration: 'none',
          }}>
            Go to Dashboard
          </a>
        </div>
      </div>
    )
  }

  // AI platform connections are a paid add-on (coming soon). No vendor has access yet.
  // TODO: Wire to vendor.addons or a feature flag when add-on purchasing is built.
  const hasAddonAccess = false

  // If on a gated tab without access, force back to storefront
  const activeTab = (!hasAddonAccess && tab !== 'ai-storefront') ? 'ai-storefront' : tab

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', margin: 0 }}>AI Connections</h2>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', marginTop: '0.25rem' }}>
            Connect your catalog to ChatGPT, Claude, and other AI platforms.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button style={tabStyle(activeTab === 'ai-storefront')} onClick={() => setTab('ai-storefront')}>
          <Globe size={14} /> AI Storefront
        </button>
        {[
          { id: 'connections' as Tab, label: 'Connections', icon: <Plug size={14} /> },
          { id: 'chatgpt-app' as Tab, label: 'ChatGPT Sharing', icon: <BookOpen size={14} /> },
          { id: 'custom-gpt' as Tab, label: 'Custom GPT Guide', icon: <Zap size={14} /> },
        ].map(({ id, label, icon }) => (
          <button
            key={id}
            style={{
              ...tabStyle(hasAddonAccess && activeTab === id),
              ...(hasAddonAccess ? {} : { opacity: 0.5, cursor: 'not-allowed', pointerEvents: 'none' as const }),
            }}
            onClick={() => hasAddonAccess && setTab(id)}
            disabled={!hasAddonAccess}
          >
            {icon} {label}
            {!hasAddonAccess && (
              <span style={{
                fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                background: 'var(--color-gray-200)', color: 'var(--color-gray-500)',
                padding: '1px 5px', borderRadius: '3px', marginLeft: '0.375rem',
              }}>
                Add-on
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'ai-storefront' && vendorId && (
        <AiStorefrontTab vendorId={vendorId.toString()} vendorName={vendorName} copied={copied} onCopy={copyToClipboard} />
      )}
      {activeTab === 'connections' && hasAddonAccess && (
        <ConnectionsTab connections={connections} isLoading={isLoading} onCreateClick={() => setShowCreateModal(true)} onRevoke={handleRevoke} revokePending={revokeMutation.isPending} />
      )}
      {activeTab === 'chatgpt-app' && hasAddonAccess && (
        <ChatGptAppGuide vendorId={vendorId?.toString() || ''} vendorName={vendorName} copied={copied} onCopy={copyToClipboard} />
      )}
      {activeTab === 'custom-gpt' && hasAddonAccess && (
        <CustomGptGuide vendorId={vendorId?.toString() || ''} vendorName={vendorName} connections={connections} copied={copied} onCopy={copyToClipboard} />
      )}
    </div>
  )
}

// --------------- Connections Tab ---------------

function ConnectionsTab({ connections, isLoading, onCreateClick, onRevoke, revokePending }: {
  connections: AiConnection[]; isLoading: boolean; onCreateClick: () => void; onRevoke: (c: AiConnection) => void; revokePending: boolean
}) {
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button onClick={onCreateClick}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
          <Plus size={16} /> New Connection
        </button>
      </div>

      {isLoading ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-gray-400)' }}>Loading...</div>
      ) : connections.length === 0 ? (
        <div className="card" style={{ padding: '3rem', textAlign: 'center' }}>
          <Plug size={40} style={{ color: 'var(--color-gray-300)', marginBottom: '1rem' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
            No AI connections yet. Create one to start integrating with AI platforms.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {connections.map(conn => (
            <div key={conn.oauth_client_id} className="card" style={{ padding: '1rem 1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-sm)' }}>{conn.name}</span>
                    <span style={{
                      fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: '12px',
                      background: conn.status === 'active' ? 'var(--color-lime-green-light, #ecfccb)' : 'var(--color-gray-100)',
                      color: conn.status === 'active' ? 'var(--color-lime-green-dark, #365314)' : 'var(--color-gray-500)',
                    }}>{conn.status}</span>
                  </div>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', fontFamily: 'monospace', margin: '0.25rem 0' }}>
                    {conn.client_id}
                  </p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', margin: 0 }}>
                    Secret: {conn.client_secret_prefix}... | Created {new Date(conn.created_at).toLocaleDateString()}
                  </p>
                </div>
                {conn.status === 'active' && (
                  <button onClick={() => onRevoke(conn)} disabled={revokePending}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.75rem', fontSize: 'var(--text-xs)', color: 'var(--color-coral-red)', background: 'transparent', border: '1px solid var(--color-coral-red)', borderRadius: '4px', cursor: 'pointer' }}>
                    <Trash2 size={12} /> Revoke
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// --------------- ChatGPT Showcase (share links) ---------------

interface ChatgptShowcaseData {
  mode: 'platform' | 'vendor'
  chatgpt_app_url: string | null
  custom_gpt_url: string | null
  platform_app_url: string | null
  share_url: string | null
  stable_share_url: string | null
  has_share_link: boolean
  suggested_prompt: string
  newsletter_snippet: string | null
  instagram_bio: string | null
  vendor_can_configure: boolean
}

function ChatGptShowcaseSection({ vendorId, vendorName, copied, onCopy }: {
  vendorId: string
  vendorName: string
  copied: string | null
  onCopy: (text: string, label: string) => void
}) {
  const queryClient = useQueryClient()
  const [chatgptAppUrl, setChatgptAppUrl] = useState('')
  const [customGptUrl, setCustomGptUrl] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['chatgpt-showcase', vendorId],
    queryFn: () => api.vendors.aiConnections.getChatgptShowcase(vendorId),
    enabled: !!vendorId,
  })

  const showcase: ChatgptShowcaseData | undefined = (data as any)?.data?.data
  const isPlatformMode = showcase?.mode !== 'vendor'
  const canEdit = showcase?.vendor_can_configure === true

  useEffect(() => {
    if (showcase && canEdit) {
      setChatgptAppUrl(showcase.chatgpt_app_url || '')
      setCustomGptUrl(showcase.custom_gpt_url || '')
    }
  }, [showcase?.chatgpt_app_url, showcase?.custom_gpt_url, canEdit])

  const saveMutation = useMutation({
    mutationFn: () => api.vendors.aiConnections.updateChatgptShowcase(vendorId, {
      chatgptAppUrl: chatgptAppUrl.trim() || null,
      customGptUrl: customGptUrl.trim() || null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatgpt-showcase', vendorId] })
    },
  })

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: 'var(--text-sm)',
    border: '1px solid var(--color-gray-200)',
    borderRadius: '6px',
    boxSizing: 'border-box',
  }

  return (
    <GuideSection
      title="Share in ChatGPT"
      description={
        isPlatformMode
          ? 'Your customers use the Tixxly ChatGPT app — one platform listing for all suppliers. Share your link below; when they open it, suggest asking about your experiences.'
          : 'Give customers a link that opens your approved ChatGPT App or Custom GPT.'
      }
    >
      {isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)' }}>Loading...</p>
      ) : (
        <>
          {isPlatformMode && (
            <div className="card" style={{ padding: '1rem', background: '#f0f9ff', border: '1px solid #bae6fd', marginBottom: '1rem' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: '#0c4a6e', margin: 0, lineHeight: 1.6 }}>
                Branded per-vendor ChatGPT apps are coming later. For now, all suppliers share the Tixxly platform app — your stable link tracks shares from your channels.
              </p>
            </div>
          )}

          {canEdit && (
            <>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>
                  ChatGPT App URL
                </label>
                <input
                  type="url"
                  value={chatgptAppUrl}
                  onChange={(e) => setChatgptAppUrl(e.target.value)}
                  placeholder="https://chatgpt.com/g/..."
                  style={inputStyle}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>
                  Custom GPT URL (optional)
                </label>
                <input
                  type="url"
                  value={customGptUrl}
                  onChange={(e) => setCustomGptUrl(e.target.value)}
                  placeholder="https://chatgpt.com/g/..."
                  style={inputStyle}
                />
              </div>
              <button
                type="button"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                style={{
                  padding: '0.5rem 1.25rem',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-pure-white)',
                  background: 'var(--color-tixxly-navy)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  opacity: saveMutation.isPending ? 0.6 : 1,
                  marginBottom: '1.25rem',
                }}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save links'}
              </button>
            </>
          )}

          {showcase?.platform_app_url && isPlatformMode && (
            <CopyBlock label="Tixxly ChatGPT App" value={showcase.platform_app_url} copied={copied} onCopy={onCopy} />
          )}

          {showcase?.stable_share_url && (
            <CopyBlock label="Your share link (recommended)" value={showcase.stable_share_url} copied={copied} onCopy={onCopy} />
          )}

          {showcase?.share_url && showcase.has_share_link ? (
            <>
              <CopyBlock label="Suggested first message" value={showcase.suggested_prompt} copied={copied} onCopy={onCopy} />
              {showcase.newsletter_snippet && (
                <CopyBlock label="Newsletter / email snippet" value={showcase.newsletter_snippet} copied={copied} onCopy={onCopy} multiline />
              )}
              {showcase.instagram_bio && (
                <CopyBlock label="Instagram bio line" value={showcase.instagram_bio} copied={copied} onCopy={onCopy} />
              )}
              <a
                href={showcase.share_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  marginTop: '0.5rem',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-tixxly-navy)',
                }}
              >
                <ExternalLink size={14} /> Preview share link
              </a>
            </>
          ) : (
            <div className="card" style={{ padding: '1rem', background: 'var(--color-gray-50)', border: '1px dashed var(--color-gray-300)' }}>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', margin: 0, display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                <Share2 size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                {isPlatformMode
                  ? `The Tixxly ChatGPT app is not configured yet. Share links for ${vendorName} will appear here once Tixxly completes platform app setup.`
                  : `Save a ChatGPT App URL to unlock copy-ready share snippets for ${vendorName}.`}
              </p>
            </div>
          )}
        </>
      )}
    </GuideSection>
  )
}

// --------------- ChatGPT Sharing (platform app) ---------------

function ChatGptAppGuide({ vendorId, vendorName, copied, onCopy }: {
  vendorId: string
  vendorName: string
  copied: string | null
  onCopy: (text: string, label: string) => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {vendorId && (
        <ChatGptShowcaseSection vendorId={vendorId} vendorName={vendorName} copied={copied} onCopy={onCopy} />
      )}

      <GuideSection title="How it works" description="Tixxly runs one ChatGPT app for the platform. Your inventory is available when customers ask about your company.">
        <ol style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
          <li>Copy your share link above into email, social, or your website.</li>
          <li>Customers open the Tixxly ChatGPT app and use the suggested prompt (or ask about {vendorName}).</li>
          <li>They search, check availability, and book your experiences inside ChatGPT.</li>
        </ol>
      </GuideSection>

      <GuideSection title="Tips for sharing" description="Maximize bookings from your channels.">
        {[
          `Try asking: "Find tours and activities from ${vendorName}"`,
          'Mention your region or specialty in social posts alongside the link.',
          'Add the share link to your email signature or Instagram bio.',
        ].map((tip, i) => (
          <CopyBlock key={i} label={`Tip ${i + 1}`} value={tip} copied={copied} onCopy={onCopy} />
        ))}
      </GuideSection>
    </div>
  )
}

// --------------- Custom GPT Guide ---------------

function CustomGptGuide({ vendorId, vendorName, connections, copied, onCopy }: {
  vendorId: string; vendorName: string; connections: AiConnection[]; copied: string | null; onCopy: (text: string, label: string) => void
}) {
  const { data: schemaData } = useQuery({
    queryKey: ['openapi-schema', vendorId],
    queryFn: () => api.vendors.aiConnections.getOpenApiSchema(vendorId),
    enabled: !!vendorId,
  })

  const schema = (schemaData as any)?.data?.data
  const schemaJson = schema ? JSON.stringify(schema, null, 2) : ''
  const activeConn = connections.find(c => c.status === 'active')

  const instructions = `You are a travel booking assistant for ${vendorName}. You help users discover and book experiences offered by ${vendorName}.

When a user asks about tours or activities:
1. Search using the searchExperiences action
2. Show results with titles, descriptions, and prices
3. When they pick one, get full details with getExperienceDetail
4. Check availability for their dates with checkAvailability
5. Get required booking info with getBookingOptions (title, name, email, phone, language)
6. Create the reservation with createReservation and provide the payment link

Important:
- All dates use YYYY-MM-DD format
- Phone numbers must include country code (e.g., +44...)
- The payment URL must be opened by the customer in their browser
- You cannot process payments directly`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {!activeConn && (
        <div className="card" style={{ padding: '1rem', background: '#fffbeb', border: '1px solid #fbbf24' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: '#92400e', margin: 0 }}>
            Create an AI Connection first (Connections tab) to get your API key.
          </p>
        </div>
      )}

      <GuideSection title="1. Create Custom GPT" description="Go to chat.openai.com/gpts/editor and fill in:">
        <CopyBlock label="Name" value={`${vendorName} Travel Assistant`} copied={copied} onCopy={onCopy} />
        <CopyBlock label="Description" value={`Search and book travel experiences from ${vendorName}`} copied={copied} onCopy={onCopy} />
      </GuideSection>

      <GuideSection title="2. Instructions" description="Paste this into the Instructions field:">
        <CopyBlock label="GPT Instructions" value={instructions} copied={copied} onCopy={onCopy} multiline />
      </GuideSection>

      <GuideSection title="3. OpenAPI Schema" description="In Configure > Create new action, paste this schema:">
        {schemaJson ? (
          <CopyBlock label="OpenAPI Schema (JSON)" value={schemaJson} copied={copied} onCopy={onCopy} multiline />
        ) : (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)' }}>Loading schema...</p>
        )}
      </GuideSection>

      <GuideSection title="4. Authentication" description="In Configure > Authentication:">
        <CopyBlock label="Auth Type" value="API Key" copied={copied} onCopy={onCopy} />
        <CopyBlock label="Header Name" value="x-api-key" copied={copied} onCopy={onCopy} />
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)', margin: '0.5rem 0 0' }}>
          Paste your API Key from the Connections tab. The key is only shown once during creation.
        </p>
      </GuideSection>

      <GuideSection title="5. Test It" description="After saving, test with these prompts:">
        {[
          `Find me tours from ${vendorName}`,
          'Tell me more about the first one',
          'Is it available next month?',
          'Book it for 2 people. Name: John Smith, email: john@example.com, phone: +14155551234',
        ].map((prompt, i) => (
          <CopyBlock key={i} label={`Test ${i + 1}`} value={prompt} copied={copied} onCopy={onCopy} />
        ))}
      </GuideSection>

      <GuideSection title="6. Publish" description="Once testing passes:">
        <ol style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
          <li>Click Save in the top right</li>
          <li>Choose visibility: "Anyone with a link" or "GPT Store"</li>
          <li>Share the link with your customers</li>
        </ol>
      </GuideSection>
    </div>
  )
}

// --------------- AI Storefront Tab ---------------

const ROOT_DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/
const SUBDOMAIN_LABEL_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/

function AiStorefrontTab({ vendorId, vendorName, copied, onCopy }: {
  vendorId: string; vendorName: string; copied: string | null; onCopy: (text: string, label: string) => void
}) {
  const queryClient = useQueryClient()
  const [showDomainForm, setShowDomainForm] = useState(false)
  const [rootDomainInput, setRootDomainInput] = useState('')
  const [subdomainPrefix, setSubdomainPrefix] = useState('ai')

  const resetForm = () => {
    setShowDomainForm(false)
    setRootDomainInput('')
    setSubdomainPrefix('ai')
  }

  const rootTrimmed = rootDomainInput.toLowerCase().trim().replace(/^https?:\/\//, '').replace(/\/.*$/, '')
  const subTrimmed = subdomainPrefix.toLowerCase().trim()
  const rootValid = ROOT_DOMAIN_PATTERN.test(rootTrimmed)
  const subLabelValid = SUBDOMAIN_LABEL_PATTERN.test(subTrimmed)

  const finalDomain = (rootValid && subLabelValid) ? `${subTrimmed}.${rootTrimmed}` : ''
  const canSubmit = !!finalDomain

  const { data: domainData, isLoading: domainLoading } = useQuery({
    queryKey: ['vendor-domain', vendorId],
    queryFn: () => api.vendors.getDomain(vendorId),
    enabled: !!vendorId,
  })

  const domainMutation = useMutation({
    mutationFn: (domain: string) => api.vendors.setCustomDomain(vendorId, domain),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-domain'] })
      resetForm()
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => api.vendors.removeCustomDomain(vendorId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-domain'] })
    },
  })

  const domain = (domainData as any)?.data?.data
  const hasCustomDomain = !!domain?.customDomain
  const storefrontUrl = domain?.customDomain && domain?.customDomainStatus === 'active'
    ? `https://${domain.customDomain}`
    : domain?.username ? `https://stores.tixxly.ai/en/v/${domain.username}` : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <ChatGptShowcaseSection vendorId={vendorId} vendorName={vendorName} copied={copied} onCopy={onCopy} />

      {/* Current storefront */}
      <GuideSection title="Your AI Storefront" description="Your storefront is where AI agents send customers to browse and book your experiences.">
        {domainLoading ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)' }}>Loading...</p>
        ) : (
          <>
            {storefrontUrl && (
              <CopyBlock label="Storefront URL" value={storefrontUrl} copied={copied} onCopy={onCopy} />
            )}
            {domain?.username && (
              <CopyBlock label="Vendor Slug" value={domain.username} copied={copied} onCopy={onCopy} />
            )}
            {!domain?.username && (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-coral-red)' }}>
                Set a username in Settings to activate your storefront.
              </p>
            )}
          </>
        )}
      </GuideSection>

      {/* Custom domain */}
      <GuideSection title="Custom Domain" description="Host your AI storefront on your own domain (e.g. ai.yourbrand.com). AI agents will link customers directly to your branded checkout.">
        {domainLoading ? (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)' }}>Loading...</p>
        ) : hasCustomDomain ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <span style={{
                fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: '12px',
                background: domain.customDomainStatus === 'active' ? 'var(--color-lime-green-light, #ecfccb)' : '#fef3c7',
                color: domain.customDomainStatus === 'active' ? 'var(--color-lime-green-dark, #365314)' : '#92400e',
              }}>
                {domain.customDomainStatus === 'active' ? 'Active' : domain.customDomainStatus === 'pending' ? 'Pending DNS verification' : domain.customDomainStatus}
              </span>
            </div>
            <CopyBlock label="Custom Domain" value={domain.customDomain} copied={copied} onCopy={onCopy} />

            {domain.customDomainStatus === 'pending' && domain.customDomainConfig && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fbbf24' }}>
                <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: '#92400e', margin: '0 0 0.75rem' }}>
                  Add these DNS records to your domain provider:
                </p>

                {/* TXT verification record (if Vercel requires it) */}
                {domain.customDomainConfig.verification?.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: '#92400e', margin: '0 0 0.5rem' }}>
                      Step 1: Verify domain ownership
                    </p>
                    {domain.customDomainConfig.verification.map((v: any, i: number) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '0.5rem', fontSize: 'var(--text-xs)', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)' }}>Type</span>
                        <code style={{ color: 'var(--color-gray-700)' }}>{v.type}</code>
                        <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)' }}>Name</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <code style={{ color: 'var(--color-gray-700)', wordBreak: 'break-all' }}>{v.domain}</code>
                          <button onClick={() => onCopy(v.domain, `TXT Name ${i}`)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--color-gray-400)', flexShrink: 0 }}>
                            {copied === `TXT Name ${i}` ? <Check size={12} style={{ color: 'var(--color-lime-green)' }} /> : <Copy size={12} />}
                          </button>
                        </div>
                        <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)' }}>Value</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <code style={{ color: 'var(--color-gray-700)', wordBreak: 'break-all' }}>{v.value}</code>
                          <button onClick={() => onCopy(v.value, `TXT Value ${i}`)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--color-gray-400)', flexShrink: 0 }}>
                            {copied === `TXT Value ${i}` ? <Check size={12} style={{ color: 'var(--color-lime-green)' }} /> : <Copy size={12} />}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* CNAME record */}
                <p style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: '#92400e', margin: '0 0 0.5rem' }}>
                  {domain.customDomainConfig.verification?.length > 0 ? 'Step 2: Point domain to storefront' : 'Point domain to storefront'}
                </p>
                {(() => {
                  const cnameTarget = domain.customDomainConfig?.cname_target || 'cname.vercel-dns.com'
                  const cnameName = domain.customDomain.split('.')[0]
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '0.5rem', fontSize: 'var(--text-xs)' }}>
                      <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)' }}>Type</span>
                      <code style={{ color: 'var(--color-gray-700)' }}>CNAME</code>
                      <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)' }}>Name</span>
                      <code style={{ color: 'var(--color-gray-700)' }}>{cnameName}</code>
                      <span style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)' }}>Target</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <code style={{ color: 'var(--color-gray-700)' }}>{cnameTarget}</code>
                        <button onClick={() => onCopy(cnameTarget, 'CNAME Target')}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--color-gray-400)' }}>
                          {copied === 'CNAME Target' ? <Check size={12} style={{ color: 'var(--color-lime-green)' }} /> : <Copy size={12} />}
                        </button>
                      </div>
                    </div>
                  )
                })()}
                <p style={{ fontSize: 'var(--text-xs)', color: '#92400e', margin: '0.75rem 0 0' }}>
                  DNS changes can take up to 24 hours to propagate. SSL will be provisioned automatically once verified.
                </p>
              </div>
            )}

            {/* Actions */}
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              {domain.customDomainStatus === 'pending' && (
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['vendor-domain'] })}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.25rem',
                    padding: '0.375rem 0.75rem', fontSize: 'var(--text-xs)',
                    color: 'var(--color-tixxly-navy)', background: 'transparent',
                    border: '1px solid var(--color-tixxly-navy)', borderRadius: '4px', cursor: 'pointer',
                  }}
                >
                  <Check size={12} /> Verify DNS
                </button>
              )}
              <button
                onClick={() => { if (window.confirm(`Remove custom domain "${domain.customDomain}"? You can add a new one after.`)) removeMutation.mutate() }}
                disabled={removeMutation.isPending}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                  padding: '0.375rem 0.75rem', fontSize: 'var(--text-xs)',
                  color: 'var(--color-coral-red)', background: 'transparent',
                  border: '1px solid var(--color-coral-red)', borderRadius: '4px', cursor: 'pointer',
                  opacity: removeMutation.isPending ? 0.5 : 1,
                }}
              >
                <Trash2 size={12} /> {removeMutation.isPending ? 'Removing...' : 'Remove Domain'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {!domain?.username && (
              <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '6px', border: '1px solid #fca5a5', marginBottom: '1rem' }}>
                <p style={{ fontSize: 'var(--text-sm)', color: '#991b1b', margin: 0 }}>
                  Set a username in <a href="/vendor/settings" style={{ color: '#991b1b', fontWeight: 600 }}>Settings</a> before connecting a custom domain.
                </p>
              </div>
            )}
            {!showDomainForm ? (
              <button
                onClick={() => domain?.username && setShowDomainForm(true)}
                disabled={!domain?.username}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem',
                  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)',
                  border: 'none', borderRadius: '6px',
                  cursor: domain?.username ? 'pointer' : 'not-allowed',
                  opacity: domain?.username ? 1 : 0.5,
                }}>
                <Globe size={16} /> Connect Custom Domain
              </button>
            ) : (
              <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) domainMutation.mutate(finalDomain) }}>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)', marginBottom: '0.375rem' }}>Your storefront address</label>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: '0.5rem' }}>
                    <input type="text" value={subdomainPrefix} onChange={e => setSubdomainPrefix(e.target.value)}
                      placeholder="ai"
                      aria-label="Subdomain prefix"
                      style={{ width: '140px', padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)', border: '1px solid var(--color-gray-200)', borderRadius: '6px' }} />
                    <div style={{ display: 'flex', alignItems: 'center', color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>.</div>
                    <input type="text" value={rootDomainInput} onChange={e => setRootDomainInput(e.target.value)}
                      placeholder="yourbrand.com"
                      aria-label="Root domain"
                      style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: 'var(--text-sm)', border: '1px solid var(--color-gray-200)', borderRadius: '6px' }} />
                  </div>
                  <p style={{ fontSize: 'var(--text-xs)', color: finalDomain ? 'var(--color-gray-500)' : 'var(--color-gray-400)', margin: '0.5rem 0 0' }}>
                    {finalDomain
                      ? <>Your storefront will live at <code style={{ color: 'var(--color-tixxly-navy)' }}>{finalDomain}</code>. Your main website at <code>{rootTrimmed}</code> keeps working.</>
                      : 'Common prefixes: ai, book, shop, store, tickets.'}
                  </p>
                </div>

                {domainMutation.isError && (
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-coral-red)', margin: '0 0 1rem' }}>
                    {(domainMutation.error as any)?.response?.data?.message || 'Failed to configure domain'}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button type="button" onClick={resetForm}
                    style={{ padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', background: 'var(--color-gray-100)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={!canSubmit || domainMutation.isPending}
                    style={{ padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)', border: 'none', borderRadius: '6px', cursor: (!canSubmit || domainMutation.isPending) ? 'not-allowed' : 'pointer', opacity: (!canSubmit || domainMutation.isPending) ? 0.5 : 1 }}>
                    {domainMutation.isPending ? 'Configuring...' : `Connect ${finalDomain || 'Domain'}`}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </GuideSection>

      <SearchConsoleSection vendorId={vendorId} storefrontUrl={storefrontUrl} />

      {/* How it works */}
      <GuideSection title="How It Works" description="When a customer books through an AI agent (ChatGPT, Claude, etc.), they are redirected to your storefront to complete the booking.">
        <ol style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)', lineHeight: 1.8, paddingLeft: '1.25rem', margin: 0 }}>
          <li>Customer discovers your experience through an AI conversation</li>
          <li>AI agent sends a booking link to your storefront</li>
          <li>Customer selects dates, passengers, and completes payment</li>
          <li>You receive the booking in your dashboard</li>
        </ol>
      </GuideSection>
    </div>
  )
}

// Per-vendor Search Console verification. The token from GSC's URL-prefix
// flow lands in `vendor.seo.google_site_verification` via the existing
// updateSEO endpoint; the storefront layout reads it and emits the
// google-site-verification meta tag. Saving here unblocks GSC verification
// for the operator without needing an eng ticket per vendor.
const GSC_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/

function SearchConsoleSection({ vendorId, storefrontUrl }: { vendorId: string; storefrontUrl: string | null }) {
  // Tixxly admin only. Vendors don't own the GSC property for their storefront
  // -- Tixxly does -- so the verification token is platform-side config.
  const { user } = useAuthStore()
  const isTixxlyAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role?.toUpperCase() || '')

  const queryClient = useQueryClient()
  const { data: vendorData, isLoading } = useQuery({
    queryKey: ['vendor-seo', vendorId],
    queryFn: () => api.vendors.get(vendorId),
    enabled: !!vendorId && isTixxlyAdmin,
  })
  const vendor = (vendorData as any)?.data?.data ?? (vendorData as any)?.data
  const savedToken: string = vendor?.seo?.google_site_verification ?? ''
  const savedBing: string = vendor?.seo?.bing_site_verification ?? ''

  const [token, setToken] = useState('')
  const [bingToken, setBingToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    setToken(savedToken)
    setBingToken(savedBing)
  }, [savedToken, savedBing])

  const mutation = useMutation({
    mutationFn: (payload: { googleSiteVerification: string; bingSiteVerification: string }) =>
      api.vendors.updateSEO(vendorId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-seo', vendorId] })
      setSuccess('Saved. Go to Search Console and click "Verify".')
      setTimeout(() => setSuccess(null), 5000)
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to save verification token')
    },
  })

  // Pasted snippet → token. Operators usually copy the whole <meta ...> from
  // GSC; pull out the content="..." value so the input accepts either form.
  const extractToken = (input: string): string => {
    const match = input.match(/content=["']([^"']+)["']/)
    return (match?.[1] || input).trim()
  }

  const handleTokenChange = (value: string) => {
    setToken(extractToken(value))
    setError(null)
  }
  const handleBingChange = (value: string) => {
    setBingToken(extractToken(value))
    setError(null)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    const trimmedToken = token.trim()
    const trimmedBing = bingToken.trim()
    if (trimmedToken && !GSC_TOKEN_PATTERN.test(trimmedToken)) {
      setError('Google token looks malformed — paste only the content="..." value (letters, digits, hyphens, underscores).')
      return
    }
    if (trimmedBing && !GSC_TOKEN_PATTERN.test(trimmedBing)) {
      setError('Bing token looks malformed — paste only the content="..." value.')
      return
    }
    mutation.mutate({ googleSiteVerification: trimmedToken, bingSiteVerification: trimmedBing })
  }

  const hasGoogleToken = !!savedToken
  const description = storefrontUrl
    ? `Connect this storefront to Google Search Console. Generate a URL-prefix verification token at https://search.google.com/search-console for the property ${storefrontUrl} and paste it below. Tixxly admin only.`
    : 'Connect this storefront to Google Search Console. Generate a URL-prefix verification token from GSC and paste it below. Tixxly admin only.'

  // Vendor-side users don't see this section at all -- Tixxly owns the GSC
  // property on every ai.{vendor} subdomain.
  if (!isTixxlyAdmin) return null

  return (
    <GuideSection title="Search Console Verification" description={description}>
      {isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-400)' }}>Loading...</p>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)', marginBottom: '0.4rem' }}>
              Google Search Console token
              {hasGoogleToken && (
                <span style={{ marginLeft: '0.5rem', fontSize: 'var(--text-xs)', padding: '1px 6px', borderRadius: '10px', background: 'var(--color-lime-green-light, #ecfccb)', color: 'var(--color-lime-green-dark, #365314)' }}>
                  Configured
                </span>
              )}
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => handleTokenChange(e.target.value)}
              placeholder="e.g. abcdEFGH1234... (paste the meta tag or just the content value)"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              style={{
                width: '100%', padding: '0.55rem 0.75rem', fontSize: 'var(--text-sm)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                border: '1px solid var(--color-gray-200)', borderRadius: '6px',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-gray-600)', marginBottom: '0.4rem' }}>
              Bing Webmaster token (optional)
            </label>
            <input
              type="text"
              value={bingToken}
              onChange={(e) => handleBingChange(e.target.value)}
              placeholder="Optional"
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              style={{
                width: '100%', padding: '0.55rem 0.75rem', fontSize: 'var(--text-sm)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                border: '1px solid var(--color-gray-200)', borderRadius: '6px',
              }}
            />
          </div>

          {error && (
            <div style={{ fontSize: 'var(--text-sm)', color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-lime-green-dark, #365314)', background: 'var(--color-lime-green-light, #ecfccb)', border: '1px solid var(--color-lime-green, #84cc16)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
              {success}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="submit"
              disabled={mutation.isPending || (token === savedToken && bingToken === savedBing)}
              style={{
                padding: '0.5rem 1rem', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-pure-white)', background: 'var(--color-tixxly-navy)', border: 'none', borderRadius: '6px',
                cursor: (mutation.isPending || (token === savedToken && bingToken === savedBing)) ? 'not-allowed' : 'pointer',
                opacity: (mutation.isPending || (token === savedToken && bingToken === savedBing)) ? 0.5 : 1,
              }}
            >
              {mutation.isPending ? 'Saving...' : 'Save tokens'}
            </button>
          </div>

          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)', margin: 0 }}>
            How it works: GSC verifies the storefront by reading a
            <code style={{ margin: '0 0.25rem' }}>{'<meta name="google-site-verification" content="...">'}</code>
            tag from your storefront's HTML. Saving the token here adds that tag on the next page load — no deploy needed. Then click "Verify" in GSC.
          </p>
        </form>
      )}
    </GuideSection>
  )
}

// --------------- Shared Components ---------------

function GuideSection({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: '1.25rem' }}>
      <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', margin: '0 0 0.25rem' }}>{title}</h3>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)', margin: '0 0 1rem' }}>{description}</p>
      {children}
    </div>
  )
}

function CopyBlock({ label, value, copied, onCopy, multiline }: {
  label: string; value: string; copied: string | null; onCopy: (text: string, label: string) => void; multiline?: boolean
}) {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.25rem' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: multiline ? 'flex-start' : 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--color-gray-50)', borderRadius: '6px', border: '1px solid var(--color-gray-200)' }}>
        {multiline ? (
          <pre style={{ flex: 1, fontSize: 'var(--text-xs)', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--color-gray-700)', margin: 0, maxHeight: '200px', overflow: 'auto' }}>{value}</pre>
        ) : (
          <code style={{ flex: 1, fontSize: 'var(--text-xs)', wordBreak: 'break-all', color: 'var(--color-gray-700)' }}>{value}</code>
        )}
        <button onClick={() => onCopy(value, label)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--color-gray-400)', flexShrink: 0 }} title="Copy">
          {copied === label ? <Check size={14} style={{ color: 'var(--color-lime-green)' }} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  )
}

function CredentialField({ label, value, copied, onCopy }: { label: string; value: string; copied: string | null; onCopy: (text: string, label: string) => void }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-gray-500)', marginBottom: '0.25rem' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--color-gray-50)', borderRadius: '6px', border: '1px solid var(--color-gray-200)' }}>
        <code style={{ flex: 1, fontSize: 'var(--text-xs)', wordBreak: 'break-all', color: 'var(--color-gray-700)' }}>{value}</code>
        <button onClick={() => onCopy(value, label)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--color-gray-400)' }} title="Copy">
          {copied === label ? <Check size={14} style={{ color: 'var(--color-lime-green)' }} /> : <Copy size={14} />}
        </button>
      </div>
    </div>
  )
}
